import {
  CSG2,
  Curve3,
  InitializeCSG2Async,
  Light,
  Material,
  Mesh,
  MeshBuilder,
  PBRMaterial,
  PhysicsAggregate,
  PhysicsShapeType,
  StandardMaterial,
  Texture,
  TransformNode,
  Vector3,
} from '@babylonjs/core';

import { GameConfig } from './GameConfig';
import { Level } from './Level';

/**
 * Types of rooms that can be generated in the dungeon
 */
export enum RoomType {
  CORRIDOR = 'corridor',
  HALL = 'hall',
}

/**
 * Interface for room wall references
 */
interface RoomWalls {
  left: Mesh;
  right: Mesh;
  front: Mesh;
  back: Mesh;
  floor: Mesh;
  ceiling: Mesh;
}

/**
 * Represents a room in the dungeon
 */
export class DungeonRoom {
  node: TransformNode;
  walls: RoomWalls;
  connections: { width: number; height: number; position: Vector3; to: DungeonRoom }[] = [];
  wallExclusions: string[] = [];
  lastDirection?: Vector3; // Direction used to connect to this room

  constructor(
    public position: Vector3,
    public size: Vector3,
    public type: RoomType,
    public connectedRooms: DungeonRoom[] = [],
    public rotation: Vector3 = new Vector3(0, 0, 0),
    public material?: string
  ) {}

  /**
   * Creates a corridor room connecting two rooms
   */
  static connectRooms(parent: DungeonRoom, child: DungeonRoom, config: DungeonConfig): DungeonRoom {
    const parentConnection = parent.connections.find((o) => o.to === child);
    const childConnection = child.connections.find((o) => o.to === parent);

    if (!parentConnection || !childConnection) {
      return undefined;
    }

    // Convert local opening positions to world coordinates
    const openingParentWorld = this.localToWorld(parentConnection.position, parent);
    const openingChildWorld = this.localToWorld(childConnection.position, child);

    // Use the stored opening dimensions from the connection
    const corridorWidth = parentConnection.width;
    const corridorHeight = parentConnection.height;

    // Calculate corridor size based on main direction
    const diffX = Math.abs(openingChildWorld.x - openingParentWorld.x);
    const diffZ = Math.abs(openingChildWorld.z - openingParentWorld.z);

    // All room floors are at Y=0, so corridor center Y is simply corridorHeight/2
    const corridorCenterY = corridorHeight / 2;

    // Position for the center of the corridor
    const position = new Vector3(
      (openingParentWorld.x + openingChildWorld.x) / 2,
      corridorCenterY,
      (openingParentWorld.z + openingChildWorld.z) / 2
    );

    let size: Vector3;
    const rotation = new Vector3(0, 0, 0);
    const wallExclusions = [];

    // Add extra length to corridor to ensure it connects properly with walls
    // The opening position is at the wall center, so we need to extend by wallThickness
    const extensionLength = config.wallThickness * 2;

    if (diffX >= diffZ) {
      // X direction is dominant (East-West corridor)
      size = new Vector3(diffX + extensionLength, corridorHeight, corridorWidth);
      wallExclusions.push('left', 'right');
    } else {
      // Z direction is dominant (North-South corridor)
      size = new Vector3(corridorWidth, corridorHeight, diffZ + extensionLength);
      wallExclusions.push('front', 'back');
    }

    const room = new DungeonRoom(position, size, RoomType.CORRIDOR, [], rotation, 'corridor');
    room.wallExclusions = wallExclusions;
    return room;
  }

  static localToWorld(localPos: Vector3, room: DungeonRoom): Vector3 {
    const rotatedPos = new Vector3(
      localPos.x * Math.cos(room.rotation.y) - localPos.z * Math.sin(room.rotation.y),
      localPos.y,
      localPos.x * Math.sin(room.rotation.y) + localPos.z * Math.cos(room.rotation.y)
    );
    return room.position.add(rotatedPos);
  }
}

export interface RoomMaterialsConfig {
  floor: string;
  left: string;
  right: string;
  front: string;
  back: string;
  ceiling: string;
}

/**
 * Configuration for dungeon generation
 */
export interface DungeonConfig {
  roomCount: number;

  hallMinSize: Vector3;
  hallMaxSize: Vector3;
  corridorMinSize: Vector3;
  corridorMaxSize: Vector3;

  wallThickness: number;

  pathCurveShiftFactor: number;

  pathSegmentsPerCurve: number;

  /**
   * Maximum number of connections (corridors) per room
   */
  maxConnectionsPerRoom: number;

  /**
   * If true, new rooms will use a different direction than the parent's last direction
   */
  forceDirectionChange: boolean;

  wallTextureScale: number;
  ceilingTextureScale: number;
  floorTextureScale: number;

  /**
   * Material mapping for different room types: [floor, wall, ceiling]
   */
  roomTypeMaterials: Map<RoomType, RoomMaterialsConfig>;

  /**
   * Name of the texture atlas to use for the dungeon
   */
  altlas?: string;
}

/**
 * Generates dungeon with rooms and corridors
 */
export class DungeonGenerator {
  private sectionId: number = 0;
  private rooms: DungeonRoom[] = [];
  private materials: Map<string, Material> = new Map();
  private lights: Light[] = [];
  public roomPathCorrelation: Map<DungeonRoom, number[]> = new Map();

  constructor(
    private level: Level,
    private config: DungeonConfig
  ) {
    if (this.config) {
      this.generateLayout(this.config);
    }
  }

  /**
   * Generates a complete dungeon based on the provided configuration
   */
  async generate(config: DungeonConfig): Promise<void> {
    // Initialize CSG2/Manifold library for boolean operations
    await InitializeCSG2Async();

    // Create materials
    this.createMaterials(config);

    // Build all rooms
    await this.buildRooms(config);

    // Create doorways between rooms
    this.createDoorways(config);

    // Build all corridors
    await this.buildCorridors(config);

    if (GameConfig.logDebug) {
      console.log(`Dungeon generation complete with ${this.rooms.length} rooms`);
    }
  }

  /**
   * Generates a Catmull-Rom spline path through the dungeon rooms.
   * The path goes from the center of the first room (start) to the center of the last room (goal),
   * following the floor of each room and passing through the center of doorway openings.
   */
  generatePath(yOffset?: number): Vector3[] {
    if (this.rooms.length === 0) {
      return [];
    }

    if (!yOffset) {
      yOffset = this.config.wallThickness;
    }

    // Only consider hall rooms for the path (exclude corridors)
    const hallRooms = this.rooms.filter((room) => room.type === RoomType.HALL);

    if (hallRooms.length < 2) {
      // If there's only one hall, return its center at floor level
      const room = hallRooms[0] || this.rooms[0];
      const floorY = room.position.y - room.size.y / 2 + yOffset;
      return [new Vector3(room.position.x, floorY, room.position.z)];
    }

    // Build a path from the first hall to the last hall through connections
    const controlPoints: Vector3[] = [];

    // BFS to find the path from first to last hall
    const goalRoom = hallRooms[hallRooms.length - 1];
    const startRoom = this.findFurthestRoom(goalRoom, hallRooms);

    // If for some reason we default back to the first room vs last room if something fails
    const actualStart = startRoom || hallRooms[0];

    const pathRooms = this.findPathBetweenRooms(actualStart, goalRoom);

    if (pathRooms.length === 0) {
      // Fallback: just use hall centers
      for (const room of hallRooms) {
        const floorY = room.position.y - room.size.y / 2 + yOffset;
        controlPoints.push(new Vector3(room.position.x, floorY, room.position.z));
      }
    } else {
      // Control points: room centers (shifted towards openings) + corridor centers
      for (let i = 0; i < pathRooms.length; i++) {
        const room = pathRooms[i];
        const floorY = room.position.y - room.size.y / 2 + yOffset;

        if (i === 0) {
          // Start: add first room center (no shift needed)
          controlPoints.push(new Vector3(room.position.x, floorY, room.position.z));
        }

        // Add corridor center between this room and next
        if (i < pathRooms.length - 1) {
          const nextRoom = pathRooms[i + 1];

          // Calculate opening positions geometrically based on room positions
          const direction = nextRoom.position.subtract(room.position);

          let openingCurrentX = room.position.x;
          let openingCurrentZ = room.position.z;
          let openingNextX = nextRoom.position.x;
          let openingNextZ = nextRoom.position.z;

          // Determine which axis is dominant (where the corridor connects)
          if (Math.abs(direction.x) > Math.abs(direction.z)) {
            // X-axis dominant: opening is on left/right walls
            if (direction.x > 0) {
              openingCurrentX = room.position.x + room.size.x / 2;
              openingNextX = nextRoom.position.x - nextRoom.size.x / 2;
            } else {
              openingCurrentX = room.position.x - room.size.x / 2;
              openingNextX = nextRoom.position.x + nextRoom.size.x / 2;
            }
          } else {
            // Z-axis dominant: opening is on front/back walls
            if (direction.z > 0) {
              openingCurrentZ = room.position.z + room.size.z / 2;
              openingNextZ = nextRoom.position.z - nextRoom.size.z / 2;
            } else {
              openingCurrentZ = room.position.z - room.size.z / 2;
              openingNextZ = nextRoom.position.z + nextRoom.size.z / 2;
            }
          }

          // Add corridor center (midpoint between openings)
          const corridorCenterX = (openingCurrentX + openingNextX) / 2;
          const corridorCenterZ = (openingCurrentZ + openingNextZ) / 2;
          controlPoints.push(new Vector3(corridorCenterX, floorY, corridorCenterZ));

          // For intermediate rooms (not first, not last), add room center
          // shifted slightly towards the NEXT opening for smoother curves
          if (i < pathRooms.length - 2) {
            const nextNextRoom = pathRooms[i + 2];
            const nextFloorY = nextRoom.position.y - nextRoom.size.y / 2 + yOffset;

            // Calculate the next opening direction
            const nextDirection = nextNextRoom.position.subtract(nextRoom.position);

            let nextOpeningX = nextRoom.position.x;
            let nextOpeningZ = nextRoom.position.z;

            if (Math.abs(nextDirection.x) > Math.abs(nextDirection.z)) {
              if (nextDirection.x > 0) {
                nextOpeningX = nextRoom.position.x + nextRoom.size.x / 2;
              } else {
                nextOpeningX = nextRoom.position.x - nextRoom.size.x / 2;
              }
            } else {
              if (nextDirection.z > 0) {
                nextOpeningZ = nextRoom.position.z + nextRoom.size.z / 2;
              } else {
                nextOpeningZ = nextRoom.position.z - nextRoom.size.z / 2;
              }
            }

            // Shift room center towards the next opening
            const shiftedX = nextRoom.position.x + (nextOpeningX - nextRoom.position.x) * this.config.pathCurveShiftFactor;
            const shiftedZ = nextRoom.position.z + (nextOpeningZ - nextRoom.position.z) * this.config.pathCurveShiftFactor;

            controlPoints.push(new Vector3(shiftedX, nextFloorY, shiftedZ));
          }
        }

        if (i === pathRooms.length - 1) {
          // End: add last room center (no shift needed)
          controlPoints.push(new Vector3(room.position.x, floorY, room.position.z));
        }
      }
    }

    // Create the Catmull-Rom spline
    const curve = Curve3.CreateCatmullRomSpline(controlPoints, this.config.pathSegmentsPerCurve, false);
    const pathPoints = curve.getPoints();

    // Correlate path points with rooms
    this.roomPathCorrelation.clear();
    for (let i = 0; i < pathPoints.length; i++) {
      const point = pathPoints[i];
      for (const room of this.rooms) {
        if (this.isPointInRoom(point, room)) {
          if (!this.roomPathCorrelation.has(room)) {
            this.roomPathCorrelation.set(room, []);
          }
          this.roomPathCorrelation.get(room)!.push(i);
        }
      }
    }

    return pathPoints;
  }

  /**
   * Checks if a point is within the bounds of a room (ignoring Y for now, or assumming floor level)
   */
  private isPointInRoom(point: Vector3, room: DungeonRoom): boolean {
    // We transform the point to the room's local space to handle rotation easily
    // However, rooms are currently axis aligned in generation logic (rotations are 0) mostly?
    // The DungeonRoom class has a rotation property. Let's handle it properly by transforming point to local.

    // Inverse transform
    const diff = point.subtract(room.position);
    // Rotate backwards (inverse of room.rotation.y)
    const localX = diff.x * Math.cos(-room.rotation.y) - diff.z * Math.sin(-room.rotation.y);
    const localZ = diff.x * Math.sin(-room.rotation.y) + diff.z * Math.cos(-room.rotation.y);

    // Check bounds (size is full width/height/depth)
    const halfX = room.size.x / 2;
    const halfZ = room.size.z / 2;

    // Use a small epsilon or margin if needed, but strict bounds should be fine for now
    return Math.abs(localX) <= halfX && Math.abs(localZ) <= halfZ;
  }

  getRoomByPathIndex(pathIndex: number): DungeonRoom {
    for (let [room, indices] of this.roomPathCorrelation) {
      if (indices.includes(pathIndex)) {
        return room;
      }
    }
    return undefined;
  }

  /**
   * Finds the room that is furthest from the startNode in terms of graph distance (hops).
   */
  private findFurthestRoom(startNode: DungeonRoom, validRooms: DungeonRoom[]): DungeonRoom {
    const distances = new Map<DungeonRoom, number>();
    const queue: { room: DungeonRoom; dist: number }[] = [{ room: startNode, dist: 0 }];
    distances.set(startNode, 0);

    let maxDist = 0;
    let furthestRoom = startNode;

    while (queue.length > 0) {
      const { room, dist } = queue.shift()!;

      if (validRooms.includes(room)) {
        if (dist > maxDist) {
          maxDist = dist;
          furthestRoom = room;
        }
      }

      for (const neighbor of room.connectedRooms) {
        if (!distances.has(neighbor)) {
          distances.set(neighbor, dist + 1);
          queue.push({ room: neighbor, dist: dist + 1 });
        }
      }
    }

    return furthestRoom;
  }

  /**
   * Finds a path of rooms from start to end using BFS
   */
  private findPathBetweenRooms(start: DungeonRoom, end: DungeonRoom): DungeonRoom[] {
    if (start === end) {
      return [start];
    }

    const queue: { room: DungeonRoom; path: DungeonRoom[] }[] = [{ room: start, path: [start] }];
    const visited = new Set<DungeonRoom>();
    visited.add(start);

    while (queue.length > 0) {
      const { room, path } = queue.shift()!;

      for (const connectedRoom of room.connectedRooms) {
        if (visited.has(connectedRoom)) {
          continue;
        }

        const newPath = [...path, connectedRoom];

        if (connectedRoom === end) {
          return newPath;
        }

        visited.add(connectedRoom);
        queue.push({ room: connectedRoom, path: newPath });
      }
    }

    // No path found, return all halls in order
    return this.rooms.filter((room) => room.type === RoomType.HALL);
  }

  private createMaterials(config: DungeonConfig): void {
    const textureNames = new Set<string>();

    config.roomTypeMaterials.forEach((value: RoomMaterialsConfig, key: RoomType, map: Map<RoomType, RoomMaterialsConfig>) => {
      textureNames.add(value.floor);
      textureNames.add(value.left);
      textureNames.add(value.right);
      textureNames.add(value.front);
      textureNames.add(value.back);
      textureNames.add(value.ceiling);
    });

    textureNames.forEach((textureName) => {
      if (config.altlas) {
        // const texture = this.level.getAtlasByName(config.altlas).createTexture(textureName);
        // const material = this.level.game.materialService.createPBRMaterial(config.altlas + '_' + textureName, texture);
        const material = this.level.getAtlasByName(config.altlas).createPBRMaterial(textureName);
        this.materials.set(textureName, material);
      } else {
        const material = this.level.getMaterial(textureName);
        this.materials.set(textureName, material);
      }
    });

    console.log(`Created ${this.materials.size} materials`);
  }

  generateLayout(config: DungeonConfig): void {
    // Create starting hall - position so floor is at Y=0
    const startHallSize = new Vector3(
      this.level.integerInRange(config.hallMinSize.x, config.hallMaxSize.x),
      this.level.integerInRange(config.hallMinSize.y, config.hallMaxSize.y),
      this.level.integerInRange(config.hallMinSize.z, config.hallMaxSize.z)
    );

    // Position center so that floor (bottom of hall) is at Y=0
    const startHall = new DungeonRoom(
      new Vector3(0, startHallSize.y / 2, 0),
      startHallSize,
      RoomType.HALL,
      [],
      new Vector3(0, 0, 0),
      'hall'
    );

    this.rooms.push(startHall);

    // Generate additional rooms
    let roomsCreated = 1;
    let failedAttempts = 0;
    const maxFailedAttempts = 50;

    while (roomsCreated < config.roomCount && failedAttempts < maxFailedAttempts) {
      if (this.generateRoom(config)) {
        roomsCreated++;
        failedAttempts = 0;
      } else {
        failedAttempts++;
      }
    }

    console.log(`Generated ${roomsCreated} rooms after ${failedAttempts} failed attempts`);
  }

  private generateRoom(config: DungeonConfig): boolean {
    const maxAttempts = 10;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      // Select random parent room that hasn't reached max connections
      const availableParents = this.rooms.filter((room) => room.connectedRooms.length < config.maxConnectionsPerRoom);

      if (availableParents.length === 0) {
        return false; // No room can accept more connections
      }

      const parentRoom = this.level.randomArrayElement(availableParents);

      // Generate room size
      const size = new Vector3(
        this.level.integerInRange(config.hallMinSize.x, config.hallMaxSize.x),
        this.level.integerInRange(config.hallMinSize.y, config.hallMaxSize.y),
        this.level.integerInRange(config.hallMinSize.z, config.hallMaxSize.z)
      );

      // Calculate corridor length
      const corridorLength = this.level.integerInRange(config.corridorMinSize.z, config.corridorMaxSize.z);

      // Get direction - either random or forced to be different from parent's last direction
      let direction: Vector3;
      if (config.forceDirectionChange && parentRoom.lastDirection) {
        // Get all directions except the one the parent used
        const allDirections = [new Vector3(1, 0, 0), new Vector3(-1, 0, 0), new Vector3(0, 0, 1), new Vector3(0, 0, -1)];
        const availableDirections = allDirections.filter(
          (d) => !d.equals(parentRoom.lastDirection) && !d.equals(parentRoom.lastDirection.scale(-1))
        );
        direction = this.level.randomArrayElement(availableDirections);
      } else {
        direction = this.level.randomDirection();
      }

      const distance = parentRoom.size.x / 2 + size.x / 2 + corridorLength;
      const offsetXZ = direction.scale(distance);

      // Position new room so its floor is at Y=0 (same as all other rooms)
      const position = new Vector3(
        parentRoom.position.x + offsetXZ.x,
        size.y / 2, // Center Y so floor is at Y=0
        parentRoom.position.z + offsetXZ.z
      );

      // Create new room (not yet added to list)
      const newRoom = new DungeonRoom(position, size, RoomType.HALL, [], new Vector3(0, 0, 0), 'hall');
      newRoom.lastDirection = direction; // Store the direction used

      // Check for collision with existing rooms
      if (this.checkRoomCollision(newRoom, config)) {
        continue; // Try again with different position
      }

      // No collision - add the room
      newRoom.connectedRooms.push(parentRoom);
      parentRoom.connectedRooms.push(newRoom);

      this.rooms.push(newRoom);
      return true;
    }

    return false;
  }

  /**
   * Checks if a room collides with any existing room (AABB collision)
   * @param room The room to check
   * @param config Dungeon config for padding/margin
   * @returns true if collision detected, false if placement is valid
   */
  private checkRoomCollision(room: DungeonRoom, config: DungeonConfig): boolean {
    // Add padding to prevent rooms from being too close
    const padding = Math.max(config.corridorMinSize.x, config.corridorMinSize.z);

    for (const existingRoom of this.rooms) {
      if (this.roomsIntersect(room, existingRoom, padding)) {
        return true;
      }
    }
    return false;
  }

  /**
   * AABB intersection test between two rooms with padding
   */
  private roomsIntersect(roomA: DungeonRoom, roomB: DungeonRoom, padding: number): boolean {
    // Calculate half-sizes with padding
    const halfA = new Vector3(roomA.size.x / 2 + padding, roomA.size.y / 2, roomA.size.z / 2 + padding);
    const halfB = new Vector3(roomB.size.x / 2 + padding, roomB.size.y / 2, roomB.size.z / 2 + padding);

    // Check overlap on each axis
    const overlapX = Math.abs(roomA.position.x - roomB.position.x) < halfA.x + halfB.x;
    const overlapY = Math.abs(roomA.position.y - roomB.position.y) < halfA.y + halfB.y;
    const overlapZ = Math.abs(roomA.position.z - roomB.position.z) < halfA.z + halfB.z;

    // Rooms intersect if they overlap on all three axes
    return overlapX && overlapY && overlapZ;
  }

  private async buildCorridors(config: DungeonConfig): Promise<void> {
    const processed = [];
    for (const room of this.rooms) {
      for (const connectedRoom of room.connectedRooms) {
        if (processed.includes(connectedRoom) || processed.includes(room)) {
          continue;
        }
        this.buildCorridor(room, connectedRoom, config);
      }
      processed.push(room);
    }
  }

  private buildCorridor(parent: DungeonRoom, child: DungeonRoom, config: DungeonConfig): void {
    const corridor = DungeonRoom.connectRooms(parent, child, config);
    if (corridor) {
      this.rooms.push(corridor);
      this.createHollowRoom(corridor, config, RoomType.CORRIDOR);
    }
  }

  private async buildRooms(config: DungeonConfig): Promise<void> {
    for (const room of this.rooms) {
      this.createHollowRoom(room, config, RoomType.HALL);
    }
  }

  private createHollowRoom(room: DungeonRoom, config: DungeonConfig, type: RoomType): void {
    const isZWall = room.size.z < room.size.x;

    const roomMaterials = config.roomTypeMaterials.get(room.type);
    const leftWallMaterial = this.materials.get(roomMaterials.left).clone(`leftwallmaterial__${this.rooms.indexOf(room)}`);
    const rightWallMaterial = this.materials.get(roomMaterials.right).clone(`rightwallmaterial__${this.rooms.indexOf(room)}`);
    const frontWallMaterial = this.materials.get(roomMaterials.front).clone(`frontwallmaterial__${this.rooms.indexOf(room)}`);
    const backWallMaterial = this.materials.get(roomMaterials.back).clone(`backwallmaterial__${this.rooms.indexOf(room)}`);
    const floorMaterial = this.materials.get(roomMaterials.floor).clone(`floormaterial__${this.rooms.indexOf(room)}`);
    const ceilingMaterial = this.materials.get(roomMaterials.ceiling).clone(`ceilingmaterial__${this.rooms.indexOf(room)}`);

    if (!leftWallMaterial || !rightWallMaterial || !frontWallMaterial || !backWallMaterial || !floorMaterial || !ceilingMaterial) {
      console.error(`Missing materials for room ${this.rooms.indexOf(room)}`);
      return;
    }

    // Create parent node
    const roomNode = new TransformNode(`${room.type}.${this.rooms.indexOf(room)}`, this.level.game.scene);
    roomNode.position = room.position;
    roomNode.rotation = room.rotation;
    room.node = roomNode;

    // Create floor
    let floor: Mesh;
    if (!room.wallExclusions.includes('floor')) {
      floor = MeshBuilder.CreateBox(
        `${room.type}.floor.${this.rooms.indexOf(room)}`,
        { width: room.size.x, height: this.config.wallThickness, depth: room.size.z },
        this.level.game.scene
      );
      floor.position = new Vector3(0, -room.size.y / 2 + this.config.wallThickness / 2, 0);
      floor.parent = roomNode;
      floor.material = floorMaterial;
      const ftexture = (
        floorMaterial instanceof StandardMaterial ? floorMaterial.diffuseTexture : (floorMaterial as PBRMaterial).albedoTexture
      ) as Texture;

      // For floor (top face of box): U maps to Z axis, V maps to X axis in Babylon.js
      // Use same scale factor for both to maintain square textures
      ftexture.uScale = room.size.z * config.floorTextureScale;
      ftexture.vScale = room.size.x * config.floorTextureScale;

      // if (isZWall) {
      //   ftexture.uScale = room.size.z * config.floorTextureScale;
      //   ftexture.vScale = room.size.x * config.floorTextureScale;
      // } else {
      //   ftexture.uScale = room.size.x * config.floorTextureScale;
      //   ftexture.vScale = room.size.z * config.floorTextureScale;
      // }

      if (type === RoomType.CORRIDOR) {
        floor.position.y += 0.001;
      }

      floor.checkCollisions = true;
      this.level.collisionMeshes.push(floor);
    }

    // Create ceiling
    let ceiling: Mesh;
    if (!room.wallExclusions.includes('ceiling')) {
      const depthOffset = type === RoomType.CORRIDOR ? 0.1 : 0;

      ceiling = MeshBuilder.CreateBox(
        `${room.type}.ceiling.${this.rooms.indexOf(room)}`,
        { width: room.size.x + depthOffset * 2, height: this.config.wallThickness, depth: room.size.z + depthOffset * 2 },
        this.level.game.scene
      );

      ceiling.position = new Vector3(depthOffset / 2, room.size.y / 2 - this.config.wallThickness / 2, depthOffset / 2);
      ceiling.parent = roomNode;
      ceiling.material = ceilingMaterial;

      const texture = (
        ceilingMaterial instanceof StandardMaterial ? ceilingMaterial.diffuseTexture : (ceilingMaterial as PBRMaterial).albedoTexture
      ) as Texture;

      // For ceiling (bottom face of box): U maps to Z axis, V maps to X axis in Babylon.js
      texture.uScale = room.size.z * config.ceilingTextureScale;
      texture.vScale = room.size.x * config.ceilingTextureScale;

      // if (isZWall) {
      //   texture.uScale = room.size.z * config.ceilingTextureScale;
      //   texture.vScale = room.size.x * config.ceilingTextureScale;
      // } else {
      //   texture.uScale = room.size.x * config.ceilingTextureScale;
      //   texture.vScale = room.size.z * config.ceilingTextureScale;
      // }

      ceiling.checkCollisions = true;
      this.level.collisionMeshes.push(ceiling);
    }

    // Create walls
    const leftWall = !room.wallExclusions.includes('left')
      ? this.createWall('left', room, this.config.wallThickness, leftWallMaterial, config)
      : undefined;
    const rightWall = !room.wallExclusions.includes('right')
      ? this.createWall('right', room, this.config.wallThickness, rightWallMaterial, config)
      : undefined;
    const frontWall = !room.wallExclusions.includes('front')
      ? this.createWall('front', room, this.config.wallThickness, frontWallMaterial, config)
      : undefined;
    const backWall = !room.wallExclusions.includes('back')
      ? this.createWall('back', room, this.config.wallThickness, backWallMaterial, config)
      : undefined;

    room.walls = {
      left: leftWall,
      right: rightWall,
      front: frontWall,
      back: backWall,
      floor: floor,
      ceiling: ceiling,
    };

    this.postProcessMesh(leftWall);
    this.postProcessMesh(rightWall);
    this.postProcessMesh(floor);
    this.postProcessMesh(ceiling);
    this.postProcessMesh(frontWall);
    this.postProcessMesh(backWall);
  }

  postProcessMesh(mesh: Mesh) {
    if (mesh) {
      if (!mesh.metadata) {
        mesh.metadata = {};
      }
      mesh.metadata.projectileCollision = true;
      mesh.receiveShadows = true;
      new PhysicsAggregate(
        mesh,
        mesh.metadata?.shapeType ? mesh.metadata.shapeType : PhysicsShapeType.BOX,
        { mass: 0 },
        this.level.game.scene
      );
    }
  }

  private createWall(side: string, room: DungeonRoom, thickness: number, material: Material, config: DungeonConfig): Mesh {
    let wall: Mesh;

    switch (side) {
      case 'left':
        wall = MeshBuilder.CreateBox(
          `wall_${side}_${this.rooms.indexOf(room)}`,
          { width: thickness, height: room.size.y, depth: room.size.z },
          this.level.game.scene
        );
        wall.position = new Vector3(-room.size.x / 2 + thickness / 2, 0, 0);
        break;

      case 'right':
        wall = MeshBuilder.CreateBox(
          `wall_${side}_${this.rooms.indexOf(room)}`,
          { width: thickness, height: room.size.y, depth: room.size.z },
          this.level.game.scene
        );
        wall.position = new Vector3(room.size.x / 2 - thickness / 2, 0, 0);
        break;

      case 'front':
        wall = MeshBuilder.CreateBox(
          `wall_${side}_${this.rooms.indexOf(room)}`,
          { width: room.size.x, height: room.size.y, depth: thickness },
          this.level.game.scene
        );
        wall.position = new Vector3(0, 0, room.size.z / 2 - thickness / 2);
        break;

      case 'back':
        wall = MeshBuilder.CreateBox(
          `wall_${side}_${this.rooms.indexOf(room)}`,
          { width: room.size.x, height: room.size.y, depth: thickness },
          this.level.game.scene
        );
        wall.position = new Vector3(0, 0, -room.size.z / 2 + thickness / 2);
        break;

      default:
        throw new Error(`Invalid wall side: ${side}`);
    }

    wall.parent = room.node;

    const wallMaterial = material.clone(`material_${side}_${this.rooms.indexOf(room)}`);
    const texture = (
      wallMaterial instanceof StandardMaterial ? wallMaterial.diffuseTexture : (wallMaterial as PBRMaterial).albedoTexture
    ) as Texture;

    // UV mapping with rotation for side walls
    if (side === 'left' || side === 'right') {
      // Side walls
      texture.uScale = room.size.y * config.wallTextureScale;
      texture.vScale = room.size.z * config.wallTextureScale;
      texture.wAng = Math.PI / 2;
    } else {
      // Front/back walls
      texture.vScale = room.size.y * config.wallTextureScale;
      texture.uScale = room.size.x * config.wallTextureScale;
    }

    wall.material = wallMaterial;

    wall.checkCollisions = true;
    this.level.collisionMeshes.push(wall);

    return wall;
  }

  private createDoorways(config: DungeonConfig): void {
    for (const room of this.rooms) {
      for (const connectedRoom of room.connectedRooms) {
        if (this.rooms.indexOf(room) < this.rooms.indexOf(connectedRoom)) {
          this.createDoorway(room, connectedRoom, config);
        }
      }
    }
  }

  private createDoorway(room1: DungeonRoom, room2: DungeonRoom, config: DungeonConfig): void {
    const direction = room2.position.subtract(room1.position);
    direction.normalize();

    let wall1: Mesh, wall2: Mesh;

    if (Math.abs(direction.x) > Math.abs(direction.z)) {
      if (direction.x > 0) {
        wall1 = room1.walls.right;
        wall2 = room2.walls.left;
      } else {
        wall1 = room1.walls.left;
        wall2 = room2.walls.right;
      }
    } else {
      if (direction.z > 0) {
        wall1 = room1.walls.front;
        wall2 = room2.walls.back;
      } else {
        wall1 = room1.walls.back;
        wall2 = room2.walls.front;
      }
    }

    // Get opening dimensions from corridor config - generate ONCE for both walls
    const openingWidth = this.level.integerInRange(config.corridorMinSize.x, config.corridorMaxSize.x);
    const openingHeight = this.level.integerInRange(config.corridorMinSize.y, config.corridorMaxSize.y);

    // Use the smaller hall height if opening would be taller than a hall
    const maxOpeningHeight = Math.min(room1.size.y, room2.size.y, openingHeight);

    this.createWallOpening(wall1, openingWidth, maxOpeningHeight, room1, room2, config);
    this.createWallOpening(wall2, openingWidth, maxOpeningHeight, room2, room1, config);
  }

  /**
   * Creates an opening in a wall using CSG2 boolean subtraction
   */
  private createWallOpening(wall: Mesh, width: number, height: number, from: DungeonRoom, to: DungeonRoom, config: DungeonConfig): void {
    if (!wall) return;

    const wallSize = wall.getBoundingInfo().boundingBox.extendSize.scale(2);
    const wallThickness = Math.min(wallSize.x, wallSize.z);
    const wallHeight = wallSize.y;
    const isZWall = wallSize.z < wallSize.x;

    const wallPos = wall.position.clone();

    // Opening starts at floor level
    const openingCenterY = wallPos.y - wallHeight / 2 + height / 2;

    // Store connection info
    from.connections.push({
      width: width,
      height: height,
      to: to,
      position: new Vector3(wallPos.x, openingCenterY, wallPos.z),
    });

    // Create opening box - the shape to cut out
    // Add small buffer to ensure clean cut through the wall
    const openingBoxSize = {
      width: isZWall ? width + 0.02 : wallThickness + 0.04,
      height: height + 0.02,
      depth: isZWall ? wallThickness + 0.04 : width + 0.02,
    };

    const opening = MeshBuilder.CreateBox('opening_cutout', openingBoxSize, this.level.game.scene);

    // Position opening at wall center, at floor level (in room-local coordinates)
    opening.position = new Vector3(wallPos.x, openingCenterY, wallPos.z);
    opening.parent = wall.parent;

    // Store wall's parent for later
    const wallParent = wall.parent as TransformNode;

    // Bake transforms into vertices so CSG sees correct world positions
    wall.bakeCurrentTransformIntoVertices();
    opening.bakeCurrentTransformIntoVertices();

    // Perform CSG subtraction - vertices are now in world space
    const wallCSG = CSG2.FromMesh(wall, true);
    const openingCSG = CSG2.FromMesh(opening, true);
    const resultCSG = wallCSG.subtract(openingCSG);

    // Create result mesh
    const resultMesh = resultCSG.toMesh(wall.name + '_opening', this.level.game.scene);
    resultMesh.parent = wallParent;
    resultMesh.position = wallPos;
    resultMesh.material = wall.material;
    resultMesh.checkCollisions = true;
    resultMesh.metadata = { shapeType: PhysicsShapeType.MESH };

    // Update room's wall reference
    const roomIndex = this.rooms.indexOf(from);
    if (roomIndex >= 0 && from.walls) {
      if (wall === from.walls.front) from.walls.front = resultMesh;
      else if (wall === from.walls.back) from.walls.back = resultMesh;
      else if (wall === from.walls.left) from.walls.left = resultMesh;
      else if (wall === from.walls.right) from.walls.right = resultMesh;
    }

    // Add to collision meshes
    this.level.collisionMeshes.push(resultMesh);
    this.postProcessMesh(resultMesh);

    // Cleanup
    wall.dispose();
    opening.dispose();
    wallCSG.dispose();
    openingCSG.dispose();
    resultCSG.dispose();
  }
}
